import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { GanttViewMode } from '@/lib/gantt/types';

interface GanttState {
  // View settings
  viewMode: GanttViewMode | null;
  showDependencies: boolean;
  showProgress: boolean;
  showCriticalPath: boolean;
  showBaselines: boolean;

  // Scroll position (for sync with grid)
  scrollLeft: number;
  scrollTop: number;

  // Selected task (for highlighting)
  selectedTaskId: string | null;
  hoveredTaskId: string | null;

  // Actions
  setViewMode: (mode: GanttViewMode) => void;
  toggleDependencies: () => void;
  toggleProgress: () => void;
  toggleCriticalPath: () => void;
  toggleBaselines: () => void;
  setScrollPosition: (left: number, top: number) => void;
  setSelectedTaskId: (taskId: string | null) => void;
  setHoveredTaskId: (taskId: string | null) => void;
  reset: () => void;
}

const initialState = {
  viewMode: null as GanttViewMode | null,
  showDependencies: true,
  showProgress: true,
  showCriticalPath: true,
  showBaselines: false,
  scrollLeft: 0,
  scrollTop: 0,
  selectedTaskId: null as string | null,
  hoveredTaskId: null as string | null,
};

export const useGanttStore = create<GanttState>()(
  persist(
    (set) => ({
      ...initialState,

      setViewMode: (mode) => set({ viewMode: mode }),

      toggleDependencies: () =>
        set((state) => ({ showDependencies: !state.showDependencies })),

      toggleProgress: () =>
        set((state) => ({ showProgress: !state.showProgress })),

      toggleCriticalPath: () =>
        set((state) => ({ showCriticalPath: !state.showCriticalPath })),

      toggleBaselines: () =>
        set((state) => ({ showBaselines: !state.showBaselines })),

      setScrollPosition: (left, top) =>
        set({ scrollLeft: left, scrollTop: top }),

      setSelectedTaskId: (taskId) => set({ selectedTaskId: taskId }),

      setHoveredTaskId: (taskId) => set({ hoveredTaskId: taskId }),

      reset: () => set(initialState),
    }),
    {
      name: 'gantt-settings',
      partialize: (state) => ({
        viewMode: state.viewMode,
        showDependencies: state.showDependencies,
        showProgress: state.showProgress,
        showCriticalPath: state.showCriticalPath,
        showBaselines: state.showBaselines,
      }),
    }
  )
);
